import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { and, not, or } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import * as v from 'valibot';
import { t } from 'ember-intl';
import { tracked, cached } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import CardsGeneric from '../../cards/generic.gts';
import Component from '@glimmer/component';
import CourseCardOverviewTable from './course-card/overview-table.gts';
import CustomFieldsApproverReview from '../../custom-fields-approver-review.gts';
import { divide } from 'tio-ui/math';
import Textarea from 'tio-ui/components/textarea';
import ProgramReadonlyField from '../../program-readonly-field.gts';
import safeFormatNumber from '../../../../helpers/safe-format-number.ts';
import type Store from '@ember-data/store';
import type TasCourseModel from 'tio-data/models/tas-course';
import type { TASCourseModelFieldsSignature } from 'tio-data/models/tas-course';
import TuitionAssistanceFormsCoursesCourseDetails from '../../forms/courses/course-details.gts';
import type TuitionAssistanceService from '../../../../services/tuition-assistance.ts';
import type {
  TasFieldsOrCustomFields,
  CourseFormField,
} from '../../../../types/tuition-assistance.ts';

import {
  buildValidationSchemaForProgramTemplateFields,
  buildValidationSchemaForCustomFields,
  buildCustomFieldsFormModelForValidation,
  getSingleValueForTasField,
  copyFieldsAndUpdatePerProgramTemplate,
  setSingleValueForTasField,
  validateAndCollectErrors,
} from '../../../../utils/tuition-assistance/fields.ts';
import DefinitionList from '../../../ui/definition-list.gts';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import type Owner from '@ember/owner';
import Button from 'tio-ui/components/button';
import { PhPencil, PhCaretDown, PhCaretUp, PhCheckCircle } from 'ember-phosphor-icons';
import type TasApplicationModel from 'tio-data/models/tas-application';

import type SessionContextService from '../../../../services/session-context.ts';
const courseAttachmentSchema = v.object({
  attachments: v.literal(true, 'Attachments are required'),
});

const changelogSchema = v.pipe(v.string('Comment is required'), v.nonEmpty('Comment is required'));
const number = Number;

export interface TuitionAssistanceProgramDetailsAppDetailsApproverCourseCardSignature {
  Args: {
    course: TasCourseModel;
    application: TasApplicationModel;
    showCustomFields?: boolean;
    approverName?: string;
  };
}

export default class TuitionAssistanceProgramDetailsAppDetailsApproverCourseCardComponent extends Component<TuitionAssistanceProgramDetailsAppDetailsApproverCourseCardSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;

  @tracked isExpanded: boolean = false;
  @tracked hasSubmitted: boolean = false;
  @tracked showEditCourseDialog: boolean = false;
  // @ts-expect-error: not sure about this one
  @tracked courseFieldsCopy: TasCourseModel['fields'] = [];
  @tracked courseCustomFieldsCopy: TasCourseModel['customFields'] = [];
  @tracked changelog: string = '';

  constructor(
    owner: Owner,
    args: TuitionAssistanceProgramDetailsAppDetailsApproverCourseCardSignature['Args']
  ) {
    super(owner, args);
    this.courseFieldsCopy = trackedObject(this.args.course.fields);
    const programTemplateCustomFields = this.programTemplate.courseCustomFields || [];

    this.courseCustomFieldsCopy = copyFieldsAndUpdatePerProgramTemplate(
      this.args.course.customFields,
      programTemplateCustomFields
    );
  }

  get programTemplate() {
    return this.args.course?.tasApplication?.tasProgramInstance?.tasProgramTemplate || {};
  }

  get courseExpenseFields(): { name: keyof TASCourseModelFieldsSignature; mask: string }[] {
    return [
      { name: 'COURSE_TUITION', mask: 'currency' },
      { name: 'COURSE_BOOKS', mask: 'currency' },
      { name: 'COURSE_LABS', mask: 'currency' },
      { name: 'COURSE_SOFTWARE', mask: 'currency' },
      { name: 'COURSE_FEES', mask: 'currency' },
    ];
  }

  // TAS.TODO - Custom course expense fields
  get courseFormFields(): CourseFormField[] {
    const numberTypeError = 'Value must be a number';
    return [
      { name: 'COURSE_NAME', mask: null, type: null, rules: { type: 'string' } },
      { name: 'COURSE_NUMBER', mask: null, type: null, rules: { type: 'string' } },
      { name: 'COURSE_DESCRIPTION', mask: null, type: null, rules: { type: 'string' } },
      {
        name: 'COURSE_CREDIT',
        mask: null,
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_TUITION',
        mask: 'currency',
        type: null,
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_BOOKS',
        mask: 'currency',
        type: null,
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_LABS',
        mask: 'currency',
        type: null,
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_SOFTWARE',
        mask: 'currency',
        type: null,
        rules: { type: 'number', typeError: numberTypeError },
      },
      {
        name: 'COURSE_FEES',
        mask: 'currency',
        type: null,
        rules: { type: 'number', typeError: numberTypeError },
      },
    ];
  }

  get requireAttachmentUploads() {
    if (this.inAttendOrPriorState) {
      return this.args.course?.tasApplication?.tasProgramInstance?.tasProgramTemplate
        ?.requireAttachmentsForCourseApproval;
    }
    return this.args.course?.tasApplication?.tasProgramInstance?.tasProgramTemplate
      ?.requireAttachmentsForCourseCompletion;
  }

  get attachmentUploadsSchema() {
    return this.requireAttachmentUploads ? courseAttachmentSchema : v.object({});
  }

  get formModel() {
    const customFieldsFormModelForProvince = buildCustomFieldsFormModelForValidation(
      this.courseCustomFieldsCopy
    );
    return {
      COURSE_NAME: getSingleValueForTasField('COURSE_NAME', this.courseFieldsCopy),
      COURSE_NUMBER: getSingleValueForTasField('COURSE_NUMBER', this.courseFieldsCopy),
      COURSE_DESCRIPTION: getSingleValueForTasField('COURSE_DESCRIPTION', this.courseFieldsCopy),
      COURSE_CREDIT: getSingleValueForTasField('COURSE_CREDIT', this.courseFieldsCopy),
      COURSE_TUITION: getSingleValueForTasField('COURSE_TUITION', this.courseFieldsCopy),
      COURSE_BOOKS: getSingleValueForTasField('COURSE_BOOKS', this.courseFieldsCopy),
      COURSE_LABS: getSingleValueForTasField('COURSE_LABS', this.courseFieldsCopy),
      COURSE_SOFTWARE: getSingleValueForTasField('COURSE_SOFTWARE', this.courseFieldsCopy),
      COURSE_FEES: getSingleValueForTasField('COURSE_FEES', this.courseFieldsCopy),
      attachments: this.hasAttachments,
      ...customFieldsFormModelForProvince,
    };
  }

  get formValidationErrors() {
    return validateAndCollectErrors(
      this.formModel,
      this.dynamicValidationSchemaForCourse,
      this.dynamicValidationSchemaForCourseCustomFields,
      this.attachmentUploadsSchema
    );
  }

  get isFormValid() {
    if (this.requireAttachmentUploads) {
      return (
        !this.formValidationErrors.length &&
        this.hasAttachments &&
        !this.changelogValidationErrors.length
      );
    }
    return !this.formValidationErrors.length && !this.changelogValidationErrors.length;
  }

  get changelogValidationErrors() {
    const result = v.safeParse(changelogSchema, this.changelog);
    if (!result.success) {
      return result.issues.map((issue) => issue.message);
    }
    return [];
  }

  @cached
  get dynamicValidationSchemaForCourse() {
    const fields = this.courseFormFields;
    const template = this.args.course?.tasApplication?.tasProgramInstance?.tasProgramTemplate;

    return buildValidationSchemaForProgramTemplateFields(fields, template);
  }

  @cached
  get dynamicValidationSchemaForCourseCustomFields() {
    return buildValidationSchemaForCustomFields(this.courseCustomFieldsCopy);
  }

  get inAttendOrPriorState() {
    return this.args.course.tasApplication.inAttendOrPriorState;
  }

  get hasAttachments() {
    if (!this.requireAttachmentUploads) {
      return true;
    }
    const { course } = this.args;
    const { tasApplication } = course;

    const assets = this.inAttendOrPriorState
      ? [course.approvalAssets, tasApplication.applicationApprovalAssets]
      : [course.evidenceAssets, tasApplication.evidenceAssets];

    return assets.some((list) => Array.isArray(list) && list.length > 0);
  }

  submit = () => {
    try {
      this.saveFields.perform();
      this.showEditCourseDialog = false;
    } catch (e) {
      console.error(e);
    }
  };

  onUpdate = (fields: TasFieldsOrCustomFields, key: string) => {
    switch (key) {
      case 'tasCourseFields':
        // @ts-expect-error: not sure about this one
        this.courseFieldsCopy = fields;
        break;
      case 'tasCourseCustomFields':
        // @ts-expect-error: not sure about this one
        this.courseCustomFieldsCopy = fields;
        break;
      default:
        console.warn(`Unexpected fieldType: ${String(key)}`);
        break;
    }
  };

  updateApprovedTotal = async (approvedTotal: number, reason: string) => {
    const updatedApplication = await this.store
      .adapterFor('tas-application')
      .updateApprovedTotal(this.args.course.tasApplication, approvedTotal, reason);

    // Instead of comparing against updatedApplication.approvedTotal directly,
    // we need to check the actual field that was updated based on the application state
    // Based on backend logic: BEFORE_PRE_APPROVAL_STATE_STRINGS includes states before ATTEND
    const beforePreApprovalStates = [
      'TAS.ApplicationState.CREATED',
      'TAS.ApplicationState.DEFINE_COURSES',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ];

    const isBeforePreApprovalState = beforePreApprovalStates.includes(
      this.args.course.tasApplication.state
    );
    const fieldToCheck = isBeforePreApprovalState ? 'PRE_APPROVED_TOTAL' : 'FINAL_APPROVED_TOTAL';
    const actualUpdatedValue = getSingleValueForTasField(
      fieldToCheck,
      updatedApplication.fields
    ) as number;

    if (actualUpdatedValue !== approvedTotal) {
      throw new Error(
        `Unable to update approved total. expected=${approvedTotal} actual=${actualUpdatedValue} (checked field: ${fieldToCheck})`
      );
    }
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  get paymentMethod() {
    return this.args.course.tasApplication.paymentMethod;
  }

  get typeClassification() {
    return this.args.course.tasApplication.typeClassification || '';
  }

  get isTypeTwoProgram() {
    return this.typeClassification === 'TAS.ProgramType.2';
  }

  setSubtotals() {
    const { requestedCourseCashSubtotalLessScholarships, requestedNonCourseCashSubtotal } =
      this.args.application;

    const courseCashTotalField = this.isTypeTwoProgram
      ? 'CARD_FINAL_APPROVED_TOTAL'
      : 'CARD_PRE_APPROVED_TOTAL';
    const nonCourseCashTotalField = this.isTypeTwoProgram
      ? 'FINAL_APPROVED_TOTAL'
      : 'PRE_APPROVED_TOTAL';

    setSingleValueForTasField(
      courseCashTotalField,
      requestedCourseCashSubtotalLessScholarships,
      this.args.application.fields
    );
    setSingleValueForTasField(
      nonCourseCashTotalField,
      requestedNonCourseCashSubtotal,
      this.args.application.fields
    );
  }

  saveFields = dropTask(async () => {
    this.hasSubmitted = true;

    if (!this.isFormValid) {
      return;
    }

    this.args.course.fields = this.courseFieldsCopy;
    this.args.course.customFields = this.courseCustomFieldsCopy;
    await this.args.course.save({ adapterOptions: { changelog: this.changelog } });
    const application = this.args.application;

    if (
      this.programTemplate.isDirectPayProgram &&
      this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
    ) {
      this.setSubtotals();
    }
    // @ts-expect-error: not sure about this one
    if (application.tasProgramInstance.calculateTotalByClaimsFinancials) {
      const calculatorResults = application.recalculateClaimsFinancialsAfterAdjacentUpdate();

      if (application.approvedTotal !== calculatorResults.CF_REIMBURSEMENT_TOTAL) {
        await this.updateApprovedTotal(calculatorResults.CF_REIMBURSEMENT_TOTAL, this.changelog);
      }
    }
    await application.save();
  });

  <template>
    <CardsGeneric>
      <:headerTitle>
        <h3 class="font-semibold leading-6">
          {{@course.displayName}}
        </h3>
      </:headerTitle>
      <:headerRight>
        {{#if (and @course.hasGradeWhenRequired this.hasAttachments)}}
          <PhCheckCircle @size="16" class="tio-fg-success -mt-4" />
        {{/if}}
      </:headerRight>
      <:body>
        <CourseCardOverviewTable @course={{@course}} />
        {{#if this.isExpanded}}
          <div class="my-4">
            <p class="font-semibold mb-1">
              {{t "tuition_assistance.program_details.courses.course_expenses_breakdown"}}
            </p>
            <DefinitionList as |options|>
              {{#each this.courseExpenseFields as |expenseField|}}
                <div class={{options.wrappingClass}}>
                  <ProgramReadonlyField
                    {{! @glint-expect-error: not sure}}
                    @name={{expenseField.name}}
                    @programTemplate={{@course.tasApplication.tasProgramInstance.tasProgramTemplate}}
                    @fields={{@course.fields}}
                    as |field|
                  >
                    <options.dt>{{field.programLabel}}</options.dt>
                    <options.dd class="flex items-start gap-x-2">
                      {{safeFormatNumber
                        (divide (or (number field.value) 0) 100)
                        style="currency"
                        currency="USD"
                      }}
                    </options.dd>
                  </ProgramReadonlyField>
                </div>
              {{/each}}
            </DefinitionList>
          </div>
          <CustomFieldsApproverReview
            @customFields={{@course.customFields}}
            @assets={{@course.tasAssets}}
            @province="COURSE"
          />
        {{/if}}
      </:body>
      <:footer>
        <div class="tio-flex-center">
          <button
            type="button"
            class="tio-text-sm-semibold text-tio-gray-600"
            {{on "click" (fn (mut this.isExpanded) (not this.isExpanded))}}
          >
            <span class="tio-row-flush">
              {{#if this.isExpanded}}
                <PhCaretDown @size="16" class="mr-1" />
              {{else}}
                <PhCaretUp @size="16" class="mr-1" />
              {{/if}}
              {{t "tuition_assistance.program_details.courses.view_course_details_and_attachments"}}
            </span>
          </button>
          <Button
            @appearance="outlined"
            @intent="primary"
            {{on "click" (fn (mut this.showEditCourseDialog) true)}}
            title={{t "common.edit"}}
            class="mx-1"
          >
            <PhPencil @size="16" />
          </Button>
        </div>
      </:footer>
    </CardsGeneric>

    <Modal
      @isOpen={{this.showEditCourseDialog}}
      @onClose={{fn (mut this.showEditCourseDialog) false}}
      class="min-h-64 !max-w-screen-lg"
      @ariaLabel={{t "common.aria_labels.edit_course"}}
    >
      <:header>
        <h3 class="font-semibold mt-2 text-midnight">
          {{t "tuition_assistance.program_details.courses.course_information"}}
        </h3>
        <h4 class="font-semibold">
          {{t "tuition_assistance.program_details.courses.please_edit_courses"}}
        </h4>
      </:header>
      <:body>
        <TuitionAssistanceFormsCoursesCourseDetails
          @course={{@course}}
          @showActionButtons={{false}}
          @hasSubmitted={{this.hasSubmitted}}
          @didUpdate={{this.onUpdate}}
          @isAdminView={{true}}
        />
        <div class="w-full md:w-64 mt-4">
          <label class="flex tio-text-sm-semibold text-tio-gray-700 block mb-1">
            {{t "comment"}}
            <span class="tio-fg-danger ml-1">*</span>
          </label>
          <Textarea
            @value={{this.changelog}}
            @onChange={{fn (mut this.changelog)}}
            @errors={{this.changelogValidationErrors}}
            @size="sm"
            class="changelog-editable__changelog-field"
          />
        </div>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.saveFields.isRunning}}
          @onSubmit={{this.submit}}
          @submitDisabled={{not this.isFormValid}}
          @onCancel={{fn (mut this.showEditCourseDialog) false}}
        />
      </:footer>
    </Modal>
  </template>
}
